import Layout from 'Nori/Layout';
import Button from 'Nori/Button';
import SplitContent from 'Custom/Split/SplitContent';
import Hero from 'Custom/Hero';
import HeroFull from 'Nori/Hero/HeroFull';
import RichText from 'Custom/RichText';
import AjustableImage from 'Custom/AjustableImage';
import ConditionsTable from 'Custom/Table/ConditionsTable';
import { Finder } from 'Custom/Finder';
import { Pregnancies } from 'Custom/Pregnancies';
import { STATEMENTS_TABLE } from 'Utils/what-is-nexplanon';
import ItemBreakpoint from 'Custom/ItemBreakpoint/index';
import ARM_IMAGE_DESKTOP from 'Images/misc/arm-split-desktop.webp';
import ARM_IMAGE_MOBILE from 'Images/misc/arm-split-mobile.webp';
import HAND_SHADOW from 'Images/misc/hand-shadow-new.png';
import HAND_SHADOW_MOBILE from 'Images/misc/hand-shadow-mobile.png';
import PLUS_IMAGE_DESKTOP from 'Images/misc/plus-sign-desktop.webp';
import PLUS_IMAGE_MOBILE from 'Images/misc/plus-sign-mobile.webp';
import HERO_BG_IMAGE from 'Images/background/dark-blue-home.png';
import HERO_BG_IMAGE_MOBILE from 'Images/background/blue-bg-mobile.png';
import styles from 'styles/pages/whatIsNexplanon.module.scss';
import HeroQuestion from 'Custom/HeroQuestion';
import EffectiveSplit from 'Custom/EffectiveSplit';

const HEAD = {
  title: 'What Is NEXPLANON® (etonogestrel implant) 68 mg Radiopaque?',
  description:
    'Learn what NEXPLANON® (etonogestrel implant) 68 mg Radiopaque is, how it works as a birth control implant, and if it is right for you.',
  keywords:
    'Nexplanon, What is Nexplanon, Birth Control Implant, Implant Birth Control',
};

const HERO_IMAGE = {
  src: HAND_SHADOW,
  alt: 'Hand Holding Implant Between Thumb and Index Finger to Show Relative Size of Implant',
};

const HERO_IMAGE_MOBILE = {
  src: HAND_SHADOW_MOBILE,
  alt: 'Hand Holding Implant Between Thumb and Index Finger to Show Relative Size of Implant',
};

const HeroBannerContent = () => {
  return (
    <Hero
      data-qa-tag="WIS-hero-component"
      variant="overlap"
      caption="Implant not actual size."
      image={HERO_IMAGE}
      mobileImage={HERO_IMAGE_MOBILE}
      className={styles.whatIsHero}
      data-design-visible="visible_vd"
      data-design-type="module"
      data-design-nori_id="Hero"
      data-design-module_name="WHAT IS NEXPLANON?"
      data-design-module_no="01"
    >
      <RichText data-qa-tag="WIS-hero-richText">
        <h4>WHAT IS NEXPLANON?</h4>
        <h2 data-qa-tag="WIS-hero-colored-title">
          <span className={styles.topColoredText}>NEXPLANON</span>
          <span data-qa-tag="WIS-hero-title">
            {' '}
            is a birth control implant that’s placed just under the skin of the
            inner, non-dominant upper arm.
          </span>
        </h2>
        <p className="body-text" data-qa-tag="WIS-hero-description-flexible">
          This flexible implant measures 1.6 inches (4 cm) in length and is
          approximately 2 mm in diameter. A trained healthcare professional will
          insert the implant into place in your inner non-dominant upper arm.
        </p>
        <p className="body-text" data-qa-tag="WIS-hero-hormone">
          Once in place, NEXPLANON releases a continuous low dose of hormone to
          help prevent pregnancy.
        </p>
      </RichText>
      <Button
        type="primary2"
        href="/how-does-nexplanon-work"
        data-design-type="clickAction"
        data-design-category="button_vd"
        data-design-action="Button"
        data-design-label="Show me how it works"
        data-design-nori_id="Hero"
        data-design-module_name="WHAT IS NEXPLANON?"
        data-design-module_no="01"
        data-qa-tag="WIS-hero-button"
      >
        Show me how it works
      </Button>
    </Hero>
  );
};

export default function Home() {
  return (
    <Layout head={HEAD}>
      <div className={styles.heroWrapper} data-qa-tag="WIS-hero-wrapper">
        <ItemBreakpoint breakpoint="lg">
          <HeroFull image={HERO_BG_IMAGE} data-qa-tag="WIS-hero-bg-desktop">
            <HeroBannerContent></HeroBannerContent>
          </HeroFull>
          <HeroFull
            image={HERO_BG_IMAGE_MOBILE}
            data-qa-tag="WIS-hero-bg-mobile"
          >
            <HeroBannerContent></HeroBannerContent>
          </HeroFull>
        </ItemBreakpoint>
      </div>
      <section
        data-qa-tag="WIS-effective-section"
        className={styles.effectiveSplitSection}
      >
        <EffectiveSplit moduleNumber="02" color={'colored'}></EffectiveSplit>
      </section>
      <SplitContent
        data-qa-tag="WIS-arm-split-wrapper"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="NEXPLANON is inserted during an outpatient office visit"
        data-design-module_no="03"
      >
        <ItemBreakpoint breakpoint="md">
          <AjustableImage
            data-qa-tag="WIS-arm-image-desktop"
            size="full"
            image={{ src: ARM_IMAGE_DESKTOP, style: { width: 292 }, alt: '' }}
          />
          <AjustableImage
            data-qa-tag="WIS-arm-image-mobile"
            image={{ src: ARM_IMAGE_MOBILE, style: { width: 146 }, alt: '' }}
          />
        </ItemBreakpoint>
        <>
          <RichText data-qa-tag="WIS-arm-content-wrapper">
            <h2 className="mb" data-qa-tag="WIS-arm-title">
              NEXPLANON is inserted during an outpatient office visit
            </h2>
            <p className="body-text" data-qa-tag="WIS-arm-description">
              Typical insertion takes a few minutes<sup>#</sup> and is performed
              under a local anesthetic. In clinical studies, 5.2% of patients
              felt insertion-site pain. NEXPLANON can be removed at any time
              during the 5-year period.
            </p>
            <p className="caption" data-qa-tag="WIS-arm-caption">
              <sup>#</sup>In a clinical trial, the mean insertion time (from the
              removal of the protection cap of the applicator until retraction
              of the needle from the arm) was 27.9 ± 29.3 seconds.
            </p>
          </RichText>
          <Button
            data-qa-tag="WIS-arm-button"
            href="/insertion/"
            data-design-type="clickAction"
            data-design-category="button_vd"
            data-design-action="Button"
            data-design-label="Learn More About Insertion"
            data-design-nori_id="Inline Content"
            data-design-module_name="NEXPLANON is inserted during an outpatient office visit"
            data-design-module_no="03"
          >
            Learn More About Insertion
          </Button>
        </>
      </SplitContent>

      <ConditionsTable
        {...STATEMENTS_TABLE}
        variant="simple"
        showIcon={false}
        className={styles.conditionsTable}
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="do one or more of these statements apply to you?"
        data-design-module_no="04"
        data-qa-tag="WIS-condition-section"
      >
        <RichText data-qa-tag="WIS-condition-content-wrapper">
          <h2 className="mb" data-qa-tag="WIS-condition-title">
            Do one or more of these statements apply to you?
          </h2>
          <p className="intro" data-qa-tag="WIS-condition-description">
            Consider talking with your doctor about NEXPLANON
          </p>
        </RichText>
      </ConditionsTable>

      <Pregnancies
        data-qa-tag="WIS-pregnancies-section"
        data-design-visible="visible_vd"
        data-design-type="module"
        data-design-nori_id="Inline Content"
        data-design-module_name="what are some birth control options?"
        data-design-module_no="05"
      />

      <section
        data-qa-tag="WIS-sideEffects-section"
        className={styles.sideEffectiosSplitSection}
      >
        <SplitContent
          data-qa-tag="WIS-sideEffects-split-wrapper"
          backgroundColor="colored"
          reverse
          data-design-visible="visible_vd"
          data-design-type="module"
          data-design-nori_id="Inline Content"
          data-design-module_name="side effects of NEXPLANON"
          data-design-module_no="06"
        >
          <ItemBreakpoint breakpoint="md">
            <AjustableImage
              data-qa-tag="WIS-sideEffects-image-desktop"
              size="full"
              image={{
                src: PLUS_IMAGE_DESKTOP,
                style: { width: 292 },
                alt: '',
              }}
            />
            <AjustableImage
              data-qa-tag="WIS-sideEffects-image-mobile"
              size="full"
              image={{ src: PLUS_IMAGE_MOBILE, alt: '' }}
            />
          </ItemBreakpoint>
          <>
            <RichText data-qa-tag="WIS-sideEffects-content-wrapper">
              <h2 data-qa-tag="WIS-sideEffects-title" className="mb">
                Side effects of NEXPLANON
              </h2>
              <p
                className="body-text"
                data-qa-tag="WIS-sideEffects-description"
              >
                The most common side effect of NEXPLANON is a change in your
                normal menstrual bleeding pattern. In studies, 1 in 10 women
                stopped using NEXPLANON because of an unfavorable change in
                their bleeding pattern. Besides changes in menstrual bleeding
                patterns, other frequent side effects that caused women to stop
                using the implant include mood swings, weight gain, headache,
                acne, and depressed mood.
              </p>
            </RichText>
            <Button
              data-qa-tag="WIS-sideEffects-button"
              href="/side-effects/"
              data-design-type="clickAction"
              data-design-category="button_vd"
              data-design-action="Button"
              data-design-label=" Side Effects of NEXPLANON"
              data-design-nori_id="Inline Content"
              data-design-module_name="side effects of NEXPLANON"
              data-design-module_no="06"
            >
              Side Effects of NEXPLANON
            </Button>
          </>
        </SplitContent>
      </section>

      <HeroQuestion
        data-design-module_no="07"
        data-qa-tag="WIS-heroQUestion-section"
      ></HeroQuestion>

      <Finder data-design-module_no="08" />
    </Layout>
  );
}
